import { z } from "zod";
import { countryRequiresPostalCode } from "@/lib/postal-codes.util";
import { i18nMessage } from "@/lib/i18nMessage";

const RatingAddressSchema = z
  .object({
    streetLines: z.array(z.string()).optional(),
    city: z.string().optional(),
    postalCode: z.string().optional(),
    countryCode: z
      .string()
      .min(1, { message: i18nMessage("boRatings:ratingSchema.countryCodeRequired") }),
    stateOrProvinceCode: z
      .string()
      .max(2, { message: i18nMessage("boRatings:ratingSchema.stateOrProvinceMaxLength") })
      .regex(/^[A-Z]*$/, {
        message: i18nMessage("boRatings:ratingSchema.stateOrProvinceUppercase"),
      })
      .optional()
      .or(z.literal("")),
  })
  .superRefine((data, ctx) => {
    if (
      (data.countryCode === "CA" || data.countryCode === "US") &&
      (!data.stateOrProvinceCode || data.stateOrProvinceCode.length !== 2)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boRatings:ratingSchema.stateOrProvinceRequired"),
        path: ["stateOrProvinceCode"],
      });
    }
    if (countryRequiresPostalCode(data.countryCode) && !data.postalCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boRatings:ratingSchema.postalCodeRequired"),
        path: ["postalCode"],
      });
    }
  });

const RatingPackageSchema = z.object({
  weight: z
    .number({ required_error: i18nMessage("boRatings:ratingSchema.weightRequired") })
    .positive({ message: i18nMessage("boRatings:ratingSchema.weightPositive") }),
  length: z
    .number({ required_error: i18nMessage("boRatings:ratingSchema.lengthRequired") })
    .positive({ message: i18nMessage("boRatings:ratingSchema.lengthPositive") })
    .optional(),
  width: z
    .number({ required_error: i18nMessage("boRatings:ratingSchema.widthRequired") })
    .positive({ message: i18nMessage("boRatings:ratingSchema.widthPositive") })
    .optional(),
  height: z
    .number({ required_error: i18nMessage("boRatings:ratingSchema.heightRequired") })
    .positive({ message: i18nMessage("boRatings:ratingSchema.heightPositive") })
    .optional(),
  value: z
    .number({ required_error: i18nMessage("boRatings:ratingSchema.valueRequired") })
    .nonnegative()
    .optional(),
});

export const CreateRatingFormSchema = z
  .object({
    shipper: RatingAddressSchema,
    recipient: RatingAddressSchema,
    totalDeclaredValue: z
      .number({ required_error: i18nMessage("boRatings:ratingSchema.totalDeclaredValueRequired") })
      .nonnegative(),
    totalWeight: z
      .number({ required_error: i18nMessage("boRatings:ratingSchema.totalWeightRequired") })
      .positive({ message: i18nMessage("boRatings:ratingSchema.totalWeightPositive") }),
    packages: z
      .array(RatingPackageSchema)
      .min(1, { message: i18nMessage("boRatings:ratingSchema.packagesMin") }),
  })
  .superRefine((data, ctx) => {
    const packagesValue = data.packages.reduce((sum, pkg) => sum + (pkg.value ?? 0), 0);
    // Rounded to cents so float accumulation (0.1 + 0.2) never triggers a false error
    const roundedPackagesValue = Math.round(packagesValue * 100) / 100;

    if (roundedPackagesValue > data.totalDeclaredValue) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("boRatings:ratingSchema.insuranceExceedsDeclaredValue", {
          amount: roundedPackagesValue,
        }),
        path: ["totalDeclaredValue"],
      });
    }
  });

export type CreateRatingFormData = z.infer<typeof CreateRatingFormSchema>;
